import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getProjectConfig } from '../../redux/selectors/projectConfig';
import TextButton from '../buttons/TextButton';
import { toggleInspiredAction, toggleUnpublishModalAction } from '../../redux/actions/projectConfig';
import CopyInput from '../inputs/CopyInput';
import LoaderIcon from '../loader/LoaderIcon';
import PrivatePassword from './PrivatePassword';
import EmbedOptions from './EmbedOptions';
import { getPublishStatus, getStatus, onPublish, onSave } from '../editor/PublishHelper';
import InspiredModal from './editor/InspiredModal';
import UnpublishModal from './UnpublishModal';
import PrimaryButton from 'shared/buttons/PrimaryButton';
import { ButtonColor, ButtonType } from 'shared/buttons/models/ButtonModels';
import VideoExport from './VideoExport';
import DownloadImage from './DownloadImage';

export default function PublishSection(props) {
  const dispatch = useDispatch();
  const { user, team } = useSelector((state) => state.profile);
  const { saved, published, publishLoading, uuid, changeMade, lastPublishTime, lastSaved } =
    useSelector(getProjectConfig);
  const { type } = useSelector((state) => state.layoutEditorPage);
  const { className, inEditor } = props;
  let shareLinkValue = `https://api.everviz.com/share/${uuid}`;
  const { maxHeight, status, text, buttonText, icon, light } = getPublishStatus(
    published,
    saved,
    changeMade,
    lastPublishTime,
    lastSaved
  );
  const isPublished = Boolean(published);
  const inVideoBeta = team && user && (team.in_beta || team.beta_features?.includes(1));
  const isLayout = type === 'layout';
  const isChart = type === 'chart';

  const onUnpublish = () => dispatch(toggleUnpublishModalAction(true));
  const handleClick = () => {
    if (light) return;
    onPublish(type, dispatch);
  };
  const save = () => onSave(type, false, dispatch);

  return (
    <div
      className={`bg-ev-light-purple p-6 rounded transition-all shrink-0 ${className} ${
        inEditor ? 'pr-8 max-h-[950px] w-72' : `overflow-hidden ${maxHeight}`
      }`}
    >
      <InspiredModal />
      <UnpublishModal />
      <h4 className={`font-bold ${inEditor ? 'pb-4' : 'pb-2'}`}>Status: {status}</h4>
      <div className="text-xs block ev-sm:hidden font-medium text-ev-navy-blue-2 pb-2">
        {getStatus(lastPublishTime, lastSaved)}
      </div>
      <div className={inEditor ? '' : 'pb-4'}>{text}</div>
      {!inEditor && (
        <div className="flex justify-between">
          <div>
            <PrimaryButton
              onClick={handleClick}
              text={
                <>
                  <LoaderIcon light={!light} icon={icon} loading={publishLoading} />
                  &nbsp;{buttonText}
                </>
              }
              buttonColor={light ? ButtonColor.White : ButtonColor.NavyBlue}
              buttonType={ButtonType.Info}
              chevron={light}
              className="text-sm"
            />
          </div>

          {!isPublished && (!saved || changeMade) && <TextButton onClick={save} text={'Save draft'} className="pt-2" />}

          {!isPublished && saved && !changeMade && (
            <span className={'font-medium pt-2'}>
              <i className="fas fa-check-circle" /> Saved
            </span>
          )}
          {isPublished && <TextButton onClick={onUnpublish} text={'Unpublish'} className="pt-2" />}
        </div>
      )}

      {inEditor && isPublished && (
        <div className={'pt-4'}>
          <TextButton onClick={onUnpublish} text={'Unpublish'} />
        </div>
      )}

      {inEditor && <hr className="border-ev-dark-purple my-6" />}

      <div
        className={`
        ${inEditor ? '' : 'pt-4'}
        ${inEditor && !saved ? 'pointer-events-none opacity-[0.6]' : ''}
        `}
      >
        <div className={`font-bold pt-2 ${inEditor ? 'pb-1' : ''}`}>Share link:</div>
        <div>
          <CopyInput value={shareLinkValue} tabIndex={saved ? null : '-1'} />
        </div>
      </div>
      <div
        className={`pt-2 
          ${inEditor && !published ? 'pointer-events-none opacity-[0.6]' : ''}
        `}
      >
        <div className={`font-bold ${inEditor ? 'pt-2 pb-4' : 'py-2'}`}>Embed to your page:</div>
        {<EmbedOptions active={published} inEditor={inEditor} />}
        <hr className={`border-ev-dark-purple ${inEditor ? 'my-6' : ''}`} />
        {type === 'chart' && (
          <>
            <PrivatePassword active={published} type={type} inEditor={inEditor} />
            <hr className={`border-ev-dark-purple ${inEditor ? 'my-6' : ''}`} />
          </>
        )}
      </div>

      {inEditor && (!isLayout || (isChart && inVideoBeta)) && (
        <>
          <div className="flex flex-col gap-2">
            {!isLayout && inEditor && (
              <DownloadImage
                defaultSelection={false}
                childrenClasses="rounded mx-4 py-2 relative flex-col gap-2"
                smallHeaderText={true}
              />
            )}
            {inEditor && isChart && inVideoBeta && <VideoExport />}
          </div>
          <hr className="border-ev-dark-purple my-6" />
        </>
      )}

      <div
        className={`
          ${inEditor && !published ? 'pointer-events-none opacity-[0.6]' : ''}
        `}
      >
        <TextButton
          onClick={() => dispatch(toggleInspiredAction(true))}
          text={'Share to everviz inspired?'}
          className="pt-4 pb-6 font-bold"
        />
      </div>
    </div>
  );
}
